import type { ResourceKey } from "i18next";

import { ErrorIcon } from "@partners/ui";
import { Button, Typo } from "@pepperstone/pepper-ui-kit";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { logger } from "@/utils/logger";

type ErrorComponentProps = {
  error?: unknown;
  errorKey?: ResourceKey;
  onClickReset?: () => void;
};

export const ErrorComponent: React.FC<ErrorComponentProps> = ({
  error,
  errorKey,
  onClickReset,
}) => {
  const { t } = useTranslation();
  const isAffiliateErrorOnMarketing =
    errorKey === "marketingPage_Affiliate_error";

  const renderErrorMessage = () => {
    if (isAffiliateErrorOnMarketing) {
      return (
        <>
          {t(errorKey)}{" "}
          <a
            href={`mailto:${import.meta.env.VITE_EMAIL_SUPPORT}`}
            style={{ color: "blue" }}
          >
            {import.meta.env.VITE_EMAIL_SUPPORT}
          </a>
        </>
      );
    }
    if (errorKey) {
      return t(errorKey);
    }
    return t("global_error_body_part1");
  };

  useEffect(() => {
    if (error) {
      const cause = error instanceof Error ? error : new Error(String(error));
      logger.error(
        new Error(`Error component displayed: ${cause.message}.`, {
          cause,
        }),
      );
    }
  }, [error]);

  return (
    <div className="mx-auto flex flex-col items-center justify-center py-8">
      <ErrorIcon />
      <Typo elem="h2" responsiveClasses="pt-6 pb-2" size="sm" type="heading">
        {t("global_error_heading")}
      </Typo>
      <Typo
        elem="p"
        responsiveClasses="pt-4 pb-4 text-center"
        size="md"
        type="body"
      >
        {renderErrorMessage()}
      </Typo>
      {onClickReset ? (
        <Button
          type="button"
          variant="ps-btn-primary"
          size="ps-btn-lg"
          onClick={onClickReset}
        >
          {t("global_error_reload_button")}
        </Button>
      ) : (
        <Button
          as="a"
          type="button"
          variant="ps-btn-primary"
          href={window.location.pathname}
          size="ps-btn-lg"
        >
          {t("global_error_reload_button")}
        </Button>
      )}
    </div>
  );
};
